import { useEffect, useState } from "react";
import swal from "sweetalert";
import { getToken, getUserId, getEmail } from "../utils/auth";
import HumanLoader from "../components/loaders/HumanLoader";
import "./ViewMcqsAnswer.css";

export default function ViewMcqsAnswer() {
  const [mcqs, setMcqs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [open, setOpen]           = useState(false);
  const [questions, setQuestions] = useState([]);
  const [index, setIndex]         = useState(0);
  const [submission, setSubmission] = useState(null);
  const [blurred, setBlurred]     = useState(false);
  const [showExp, setShowExp]     = useState(false);
  const [search, setSearch]       = useState("");

  const studentId = getUserId();
  const email     = getEmail();

  /* ---------- FETCH ---------- */
  useEffect(() => {
    async function fetchMcqs() {
      const t0 = Date.now();
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/mcqs/getMcqsDetailForStudent`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ studentId }),
          }
        );
        const data = await res.json();
        setMcqs(data || []);
      } catch {
        swal("Error", "Failed to load MCQs", "error");
      } finally {
        const remaining = Math.max(3000 - (Date.now() - t0), 0);
        setTimeout(() => setLoading(false), remaining);
      }
    }
    fetchMcqs();
  }, [studentId]);

  /* ---------- TAB BLUR ---------- */
  useEffect(() => {
    const h = () => setBlurred(document.hidden);
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
  }, []);

  /* ---------- LOCK SCROLL ---------- */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
  }, [open]);

  /* ---------- OPEN MCQ ---------- */
  const openMcq = async (mcq) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/mcq-submissions/getSubmissionForStudentAndMcq`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ studentId, mcqId: mcq._id }),
        }
      );
      const submissions = await res.json();

      if (!submissions?.length) {
        return swal({ title: "Not Attempted", text: "You have not attempted this MCQ yet.", icon: "warning", button: "OK" });
      }

      const last = submissions[0];
      const hoursPassed = (Date.now() - new Date(last.submittedAt)) / (1000 * 60 * 60);

      if (hoursPassed < 24) {
        const remainingHours = Math.ceil(24 - hoursPassed);
        return swal({
          title: "Answers Locked",
          text: `Answers will unlock after 24 hours.\n\nPlease wait ${remainingHours} more hour(s).`,
          icon: "info", button: "OK", closeOnClickOutside: false,
        });
      }

      setSubmission(last);
      setQuestions(mcq.questions || []);
      setIndex(0);
      setShowExp(false);
      setOpen(true);
    } catch {
      swal("Error", "Unable to load MCQ answers", "error");
    }
  };

  if (loading) return <HumanLoader loadingText="Preparing answer review" successText="Ready to review!" duration={2000} />;

  /* ---------- DERIVED ---------- */
  const filtered = mcqs.filter((m) =>
    m.topic?.toLowerCase().includes(search.toLowerCase()) ||
    m.category?.toLowerCase().includes(search.toLowerCase())
  );

  const totalQuestions = mcqs.reduce((a, b) => a + b.questions.length, 0);

  /* ---------- MODAL DERIVED ---------- */
  const q = questions[index];
  const studentAnswer = submission?.answers?.find(
    (a) => a.questionId?.toString() === q?._id?.toString()
  )?.selectedOption;

  const correct  = submission?.answers?.filter((a) => a.isCorrect).length ?? 0;
  const wrong    = submission?.answers?.filter((a) => a.selectedOption && !a.isCorrect).length ?? 0;
  const skipped  = submission?.answers?.filter((a) => !a.selectedOption).length ?? 0;
  const total    = questions.length;
  const pct      = total > 0 ? Math.round((correct / total) * 100) : 0;
  const circumference = 2 * Math.PI * 28;

  const scoreColor = pct >= 70 ? "#22c55e" : pct >= 40 ? "#f59e0b" : "#ef4444";
  const scoreLabel = pct >= 70 ? "Great Job!" : pct >= 40 ? "Keep Practicing" : "Needs Improvement";

  const progress = total ? ((index + 1) / total) * 100 : 0;

  const isCorrectQ  = q?.correctOption === studentAnswer;
  const isWrongQ    = studentAnswer && !isCorrectQ;
  // eslint-disable-next-line
  const isSkippedQ  = !studentAnswer;

  return (
    <div className="vma-page">

      {/* ── TOP BAR ── */}
      <div className="vma-topbar">
        <div>
          <h1 className="vma-title">MCQ Answer Review</h1>
          <p className="vma-subtitle">Review your submitted tests with correct answers and explanations</p>
        </div>

        <div className="vma-stats">
          <div className="vma-stat">
            <span className="vma-stat-val">{mcqs.length}</span>
            <span className="vma-stat-lbl">Tests</span>
          </div>
          <div className="vma-stat-div" />
          <div className="vma-stat">
            <span className="vma-stat-val">{totalQuestions}</span>
            <span className="vma-stat-lbl">Questions</span>
          </div>
        </div>
      </div>

      {/* ── SEARCH ── */}
      <div className="vma-search-wrap">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <input
          type="text"
          placeholder="Search by topic or category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ── GRID ── */}
      {filtered.length === 0 ? (
        <div className="vma-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1"
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          <p>No MCQs found</p>
        </div>
      ) : (
        <div className="vma-grid">
          {filtered.map((mcq) => (
            <div key={mcq._id} className="vma-card" onClick={() => openMcq(mcq)}>
              <div className="vma-card-accent" />
              <div className="vma-card-head">
                {mcq.category && <span className="vma-cat-chip">{mcq.category}</span>}
                <span className="vma-review-chip">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Review
                </span>
              </div>
              <h3 className="vma-card-title">{mcq.topic}</h3>
              <div className="vma-card-info">
                <span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  {mcq.questions.length} Questions
                </span>
                {mcq.duration && (
                  <span>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {mcq.duration} min
                  </span>
                )}
              </div>
              <button className="vma-card-btn">
                View Analysis
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ══════════════ MODAL ══════════════ */}
      {open && (
        <div className="vma-overlay" onClick={() => setOpen(false)}>
          <div className={`vma-modal ${blurred ? "vma-blurred" : ""}`} onClick={(e) => e.stopPropagation()}>

            {/* watermark */}
            <div className="vma-watermark">{email}</div>

            {/* ── RESULT HEADER ── */}
            <div className="vma-result-header">
              {/* score ring */}
              <div className="vma-ring-wrap">
                <svg width="72" height="72" viewBox="0 0 72 72">
                  <circle cx="36" cy="36" r="28" fill="none" stroke="#f1f5f9" strokeWidth="6"/>
                  <circle
                    cx="36" cy="36" r="28" fill="none"
                    stroke={scoreColor}
                    strokeWidth="6"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference - (pct / 100) * circumference}
                    strokeLinecap="round"
                    transform="rotate(-90 36 36)"
                    style={{ transition: "stroke-dashoffset 0.6s ease" }}
                  />
                </svg>
                <span className="vma-ring-pct" style={{ color: scoreColor }}>{pct}%</span>
              </div>

              {/* score breakdown */}
              <div className="vma-result-info">
                <p className="vma-result-label" style={{ color: scoreColor }}>{scoreLabel}</p>
                <p className="vma-result-score">
                  {submission?.score ?? correct} / {submission?.totalMarks ?? total} marks
                </p>
                <div className="vma-breakdown">
                  <span className="vma-bd-chip vma-bd-correct">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    {correct} Correct
                  </span>
                  <span className="vma-bd-chip vma-bd-wrong">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    {wrong} Wrong
                  </span>
                  <span className="vma-bd-chip vma-bd-skipped">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {skipped} Skipped
                  </span>
                </div>
              </div>

              {/* close */}
              <button className="vma-close-btn" onClick={() => setOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* ── PROGRESS BAR ── */}
            <div className="vma-prog-wrap">
              <div className="vma-prog-track">
                <div className="vma-prog-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="vma-prog-label">Q {index + 1} / {total}</span>
            </div>

            {/* ── QUESTION STATUS BADGE ── */}
            <div className="vma-q-status-row">
              <span className={`vma-q-status ${isCorrectQ ? "vma-qs-correct" : isWrongQ ? "vma-qs-wrong" : "vma-qs-skipped"}`}>
                {isCorrectQ ? "✓ You got this right" : isWrongQ ? "✗ Incorrect answer" : "— Skipped"}
              </span>
              {q?.category && <span className="vma-q-cat">{q.category}</span>}
            </div>

            {/* ── QUESTION TEXT ── */}
            <p className="vma-question">Q{index + 1}. {q?.question}</p>

            {/* ── OPTIONS ── */}
            <ul className="vma-options">
              {["A", "B", "C", "D"].map((opt) => {
                const isCorrectOpt = q?.correctOption === opt;
                const isSelected   = studentAnswer === opt;
                const isWrongOpt   = isSelected && !isCorrectOpt;

                return (
                  <li key={opt} className={`vma-option ${isCorrectOpt ? "vma-opt-correct" : ""} ${isWrongOpt ? "vma-opt-wrong" : ""}`}>
                    <span className={`vma-opt-circle ${isCorrectOpt ? "vma-oc-correct" : isWrongOpt ? "vma-oc-wrong" : ""}`}>
                      {opt}
                    </span>
                    <span className="vma-opt-text">{q?.[`option${opt}`]}</span>
                    <div className="vma-opt-tags">
                      {isCorrectOpt && (
                        <span className="vma-tag vma-tag-correct">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          Correct
                        </span>
                      )}
                      {isSelected && (
                        <span className={`vma-tag ${isCorrectOpt ? "vma-tag-yours-correct" : "vma-tag-yours-wrong"}`}>
                          Your Answer
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* ── EXPLANATION ── */}
            {!showExp ? (
              <button className="vma-reveal-btn" onClick={() => setShowExp(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Show Explanation
              </button>
            ) : (
              <div className="vma-explanation">
                <p className="vma-exp-label">Explanation</p>
                <p className="vma-exp-text">{q?.explanation || "No explanation provided."}</p>
              </div>
            )}

            {/* ── NAV ── */}
            <div className="vma-nav">
              <button
                className="vma-nav-btn"
                disabled={index === 0}
                onClick={() => { setIndex(index - 1); setShowExp(false); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                Previous
              </button>

              {/* question dots */}
              <div className="vma-dots">
                {questions.map((_, i) => {
                  const ans = submission?.answers?.find((a) => a.questionId?.toString() === questions[i]?._id?.toString());
                  const dotClass = ans?.isCorrect ? "vma-dot-correct" : ans?.selectedOption ? "vma-dot-wrong" : "vma-dot-skip";
                  return (
                    <button
                      key={i}
                      className={`vma-dot ${dotClass} ${i === index ? "vma-dot-active" : ""}`}
                      onClick={() => { setIndex(i); setShowExp(false); }}
                      title={`Q${i + 1}`}
                    />
                  );
                })}
              </div>

              <button
                className="vma-nav-btn"
                disabled={index === questions.length - 1}
                onClick={() => { setIndex(index + 1); setShowExp(false); }}
              >
                Next
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
